/** @jsxImportSource ./simboluri-jsx */
// ⚡ CENTRUL DE ACTIVITATE AL PLATFORMEI (A9): «unicul loc „ce rulează
// acum"», în antet, cu un contor pe fulger. Popover-ul grupează pe etape —
// Situație, Arhitectură, Structură — ce se lucrează ACUM, cu nume omenești
// («Soluția 2 pe A2 · Geometrie macro · trecerea 2 din 4»), nu cu felul
// jobului; fiecare linie e un link spre soluția ei. Sub ele, rulările care nu
// nasc soluții (citirea actelor, importul planșelor).
//
// Datele vin din registrul platformei (`nav/Registru`), aceleași ca ale
// lanțului și ale paginii soluțiilor; rulările fără soluție, din `listJobs`.
// Își face portal în slotul din antet (`#antet-stare`).

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useT, type Cheie } from "../i18n";
import { IcPipeline } from "./icoane";
import type { Job } from "../api";
import { jobProgress } from "../solutions/shared";
import { SemnStare } from "../components/FazeBara";
import { useRegistruOptional } from "../nav/Registru";
import { caleSolutii, inLucruAcum, numeEtapa, type LucruAcum } from "../nav/selectie";
import type { Disciplina } from "../nav/api";

/** Felurile de rulare care nu produc o soluție, cu numele lor pentru om. */
const FARA_SOLUTIE: Record<string, Cheie> = {
  extraction: "act.fel.extraction",
  external_architecture: "act.fel.external_architecture",
  basement_architecture: "act.fel.basement_architecture",
  facade: "act.fel.facade",
  recomp: "act.fel.recomp",
  geotech: "act.fel.geotech",
};

export default function ActivitateCentru({ id, jobs = [] }: {
  id: string;
  /** rulările proiectului (`listJobs`): de aici vin cele fără soluție */
  jobs?: Job[];
}) {
  const t = useT();
  const registru = useRegistruOptional()?.registru ?? null;
  const [tinta, setTinta] = useState<HTMLElement | null>(null);
  useEffect(() => setTinta(document.getElementById("antet-stare")), []);
  const [deschis, setDeschis] = useState(false);
  useEffect(() => {
    if (!deschis) return;
    const inchide = (e: Event) => {
      const tt = e.target as HTMLElement | null;
      if (tt && tt.closest(".ob-act")) return;
      setDeschis(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDeschis(false);
    };
    document.addEventListener("mousedown", inchide);
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("mousedown", inchide);
      document.removeEventListener("keydown", taste);
    };
  }, [deschis]);

  const lucru = useMemo(() => (registru ? inLucruAcum(registru, id) : []), [registru, id]);
  // Rulările vii care nu nasc soluții. Importul planșelor ARE un rând de
  // arhitectură abia la final, deci până atunci e tot aici; cel cu rând e deja
  // în lista de mai sus.
  const altele = jobs.filter((j) => (j.status === "running" || j.status === "pending")
    && !j.is_dead && FARA_SOLUTIE[j.kind]
    && !(j.solution_id && lucru.some((l) => l.id === j.solution_id)));
  const nr = lucru.length + altele.length;
  const pe = (d: Disciplina) => lucru.filter((l) => l.disciplina === d);

  const linie = (l: LucruAcum) => (
    <Link key={l.id} to={l.href} className="ob-act-linie" onClick={() => setDeschis(false)}>
      <SemnStare stare={l.stare} s={12} />
      <span>
        <b>{l.nume}</b>
        {l.pe ? ` ${l.pe}` : ""}
        {l.faza ? ` · ${l.faza}` : ""}
        {l.nota && <span className="ob-act-nota"> · {l.nota}</span>}
      </span>
    </Link>
  );

  const continut = (
    <div className="ob-act">
      <button
        className={`ob-act-buton${deschis ? " on" : ""}`}
        onClick={() => setDeschis(!deschis)}
        title={nr === 0 ? t("struct.act.buton_liber") : t("struct.act.buton_lucru", { n: nr })}
        aria-label={nr === 0 ? t("struct.act.buton_liber") : t("struct.act.buton_lucru", { n: nr })}
        aria-expanded={deschis}
      >
        <IcPipeline size={17} />
        {nr > 0 && <span className="ob-act-badge">{nr}</span>}
      </button>
      {deschis && (
        <div className="ob-card ob-act-pop">
          <div className="ob-lbl" style={{ marginBottom: 6 }}>{t("struct.act.acum")}</div>
          {nr === 0 && (
            <div style={{ fontSize: 12, color: "var(--mut)" }}>{t("struct.act.nimic")}</div>
          )}
          {(["site_plan", "architecture", "structure"] as Disciplina[]).map((d) =>
            pe(d).length > 0 && (
              <div key={d} className="ob-act-grup">
                <span className="ob-lbl">{numeEtapa(d)}</span>
                {pe(d).map(linie)}
              </div>
            ))}
          {altele.length > 0 && (
            <div className="ob-act-grup">
              <span className="ob-lbl">{t("act.dosar")}</span>
              {altele.map((j) => (
                <Link key={j.id} to={`/p/${id}/dossier`} className="ob-act-linie"
                      onClick={() => setDeschis(false)}>
                  <SemnStare stare={j.status === "pending" ? "la_rand" : "in_lucru"} s={12} />
                  <span>
                    <b>{t(FARA_SOLUTIE[j.kind])}</b>
                    <span className="ob-act-nota"> · {jobProgress(j)}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 12, borderTop: "1px solid var(--line)",
                        paddingTop: 9 }}>
            <Link to={caleSolutii(id)} style={{ fontSize: 12 }} onClick={() => setDeschis(false)}>
              {t("struct.act.link_solutii")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );

  return tinta ? createPortal(continut, tinta) : null;
}
